import React from 'react';
import {Box, ButtonBase} from '@mui/material';
import Constants from '../../../utils/constants';
import {ACTE_LIBELLES, VERDICTS, VERDICT_COULEURS, VERDICT_LIBELLES} from '../../tokens.js';
import PiluleVerdict from '../PiluleVerdict.js';
import TuileKpi from '../TuileKpi.js';
import {IconeChevronDroite, IconeChevronGauche, TITRE_SECTION_STYLE} from './CarteUi.js';
import {formatTonnage, libelleDernierPassage, libelleEtat, photosEvenement} from './carte.selectors.js';
import {nbPhotosEvenement} from '../../selectors/etat.js';

const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)} %`);
const nb = (n) => n.toLocaleString('fr-FR');

function Squelette({largeur = '100%', hauteur = 12}) {
    return <Box sx={{width: largeur, height: hauteur, backgroundColor: '#F0F2F5', borderRadius: '6px'}}/>;
}

function Classement({titre, lignes, onClic}) {
    const rangs = [0, 1, 2];
    return (
        <Box sx={{p: '14px 16px', borderBottom: '1px solid #EEF1F4', display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <Box component="h3" sx={{m: 0, fontSize: 12, fontWeight: 700}}>{titre}</Box>
            <Box component="ol" sx={{m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: 13}}>
                {rangs.map((i) => {
                    const l = lignes[i];
                    return (
                        <li key={i}>
                            <ButtonBase
                                disabled={!l}
                                onClick={() => l && onClic(l)}
                                sx={{width: '100%', justifyContent: 'space-between', fontSize: 13, color: '#2D333B', fontWeight: i === 0 && l ? 600 : 400, textAlign: 'left'}}
                            >
                                <span>{i + 1}. {l ? l.libelle : '—'}</span>
                                <span>{l ? nb(l.nb) : '—'}</span>
                            </ButtonBase>
                        </li>
                    );
                })}
            </Box>
        </Box>
    );
}

/** Bloc « PAV sélectionné » : nomenclature, commune, verdict, dernier passage, 3 vignettes max, lien vers la fiche. */
function PavSelectionne({selection, acte, onOuvrirFiche}) {
    if (!selection) {
        return (
            <Box sx={{p: '14px 16px', backgroundColor: '#F6F8F4', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px'}}>
                <Box component="h3" sx={TITRE_SECTION_STYLE}>PAV sélectionné</Box>
                <Box sx={{fontSize: 13, color: '#6B7480'}}>Cliquez sur un marqueur pour afficher le point.</Box>
            </Box>
        );
    }
    const {pav, etat} = selection;
    const photos = photosEvenement(etat.evenement);
    const vignettes = photos.slice(0, 3);
    return (
        <Box sx={{p: '14px 16px', backgroundColor: '#F6F8F4', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <Box component="h3" sx={TITRE_SECTION_STYLE}>PAV sélectionné</Box>
            <Box sx={{display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap'}}>
                <Box component="span" sx={{fontSize: 20, fontWeight: 700}}>{pav.nomenclature}</Box>
                <Box component="span" sx={{fontSize: 13, color: '#6B7480'}}>
                    {pav.communeLibelle ?? '—'}{pav.quantite != null ? ` · ${pav.quantite} abri-bac${pav.quantite > 1 ? 's' : ''}` : ''}
                </Box>
            </Box>
            <PiluleVerdict verdict={etat.verdict} libelle={libelleEtat(etat)}/>
            <Box component="dl" sx={{m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 13}}>
                <Box component="dt" sx={{color: '#6B7480'}}>{acte === 'collecte' ? 'Dernière collecte' : 'Dernier passage'}</Box>
                <Box component="dd" sx={{m: 0, fontWeight: 600}}>{libelleDernierPassage(etat)}</Box>
                <Box component="dt" sx={{color: '#6B7480'}}>Photos</Box>
                <Box component="dd" sx={{m: 0, fontWeight: 600}}>{etat.evenement && acte !== 'collecte' ? nbPhotosEvenement(etat.evenement) : '—'}</Box>
            </Box>
            {vignettes.length > 0 && (
                <Box sx={{display: 'flex', gap: '4px', mt: '2px'}}>
                    {vignettes.map((p, i) => (
                        <Box
                            key={`${p.url}-${i}`}
                            component="img"
                            src={`${Constants.API_ASSETS_PHOTO_INTERVENTION}/${p.url}`}
                            alt={p.etiquette ? `Photo ${String(p.etiquette).toLowerCase()}` : 'Photo du passage'}
                            loading="lazy"
                            sx={{width: 72, height: 54, objectFit: 'cover', border: '1px solid #E3E7EC', borderRadius: '6px', backgroundColor: '#DCE3EA'}}
                        />
                    ))}
                </Box>
            )}
            <ButtonBase
                onClick={() => onOuvrirFiche(pav.id)}
                sx={{height: 36, mt: '4px', gap: '8px', border: '1px solid #213067', backgroundColor: '#213067', color: '#FFFFFF', borderRadius: '999px', fontSize: 13, fontWeight: 600}}
            >
                Ouvrir la fiche PAV
                {IconeChevronDroite}
            </ButtonBase>
        </Box>
    );
}

/**
 * R3 : panneau gauche (340 px, repliable). Les nombres viennent de `kpi` / `repartition` (lot 2) calculés par la page.
 * `chargement` : squelette. `nbPav === 0` : compteurs à 0 et message (Carte-Vide.dc.html).
 */
export default function CartePanneau({
    chargement, replie, onReplier, nbPav, acte, k, parVerdict, defauts, communes, selection, onKpi, onElement, onCommune, onOuvrirFiche,
}) {
    if (replie) {
        return (
            <Box component="aside" aria-label="Indicateurs de la sélection (replié)" sx={{flex: '0 0 44px', backgroundColor: '#FFFFFF', borderRight: '1px solid #C4C4C4', display: 'flex', justifyContent: 'center', pt: '14px', zIndex: 2}}>
                <ButtonBase aria-label="Déplier le panneau" onClick={onReplier} sx={{width: 28, height: 28, border: '1px solid #E3E7EC', borderRadius: '999px', color: '#2D333B'}}>
                    {IconeChevronDroite}
                </ButtonBase>
            </Box>
        );
    }

    const entete = (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '10px', p: '14px 16px', borderBottom: '1px solid #EEF1F4'}}>
            <Box component="h2" sx={TITRE_SECTION_STYLE}>Indicateurs · {chargement ? '…' : `${nb(nbPav)} PAV`}</Box>
            {!chargement && <Box component="span" sx={{fontSize: 11, color: '#6B7480'}}>recalculés sur les filtres</Box>}
            <ButtonBase aria-label="Replier le panneau" onClick={onReplier} sx={{ml: 'auto', width: 28, height: 28, border: '1px solid #E3E7EC', borderRadius: '999px', color: '#2D333B'}}>
                {IconeChevronGauche}
            </ButtonBase>
        </Box>
    );

    const conteneur = {flex: '0 0 340px', width: 340, display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF', borderRight: '1px solid #C4C4C4', overflowY: 'auto', position: 'relative', zIndex: 2};

    if (chargement) {
        return (
            <Box component="aside" aria-label="Indicateurs de la sélection (chargement)" aria-busy="true" sx={conteneur}>
                {entete}
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', borderBottom: '1px solid #EEF1F4'}}>
                    {[0, 1, 2, 3].map((i) => (
                        <Box key={i} sx={{p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px', borderRight: i % 2 === 0 ? '1px solid #EEF1F4' : 'none', borderBottom: i < 2 ? '1px solid #EEF1F4' : 'none'}}>
                            <Squelette largeur="50%" hauteur={26}/>
                            <Squelette largeur="70%"/>
                        </Box>
                    ))}
                </Box>
                <Box sx={{p: '12px 16px', borderBottom: '1px solid #EEF1F4', display: 'flex', flexDirection: 'column', gap: '8px'}}><Squelette hauteur={10}/></Box>
                <Box sx={{p: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <Squelette/>
                    <Squelette largeur="90%"/>
                    <Squelette largeur="80%"/>
                </Box>
            </Box>
        );
    }

    const vide = nbPav === 0;
    const lavage = acte === 'lavage';
    const chiffreCouverture = lavage ? k.tauxService : k.couverture;
    const lavesOuInspectes = lavage ? k.ras + k.enDefaut : k.inspectes;

    return (
        <Box component="aside" aria-label="Indicateurs de la sélection" sx={conteneur}>
            {entete}
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', p: '12px', borderBottom: '1px solid #EEF1F4'}}>
                {acte === 'collecte' ? (
                    <>
                        <TuileKpi tailleChiffre={26} chiffre={vide ? null : formatTonnage(k.tonnageKg)} libelle="tonnage" sousLigne="collecté sur la plage"/>
                        <TuileKpi tailleChiffre={26} chiffre={k.nbCollectes} libelle="collectes" sousLigne="déclassés compris"/>
                        <TuileKpi
                            tailleChiffre={26}
                            chiffre={k.nbDeclasses}
                            libelle="déclassés"
                            sousLigne={k.nbCollectes > 0 ? `${pct(k.nbDeclasses / k.nbCollectes)} des collectes` : undefined}
                            onClick={vide ? undefined : () => onKpi('imp')}
                        />
                        <TuileKpi tailleChiffre={26} chiffre={null} libelle="photos" sousLigne={ACTE_LIBELLES[acte]}/>
                    </>
                ) : (
                <>
                <TuileKpi
                    tailleChiffre={26}
                    chiffre={vide ? null : pct(chiffreCouverture)}
                    libelle={lavage ? 'taux de service' : 'couverture'}
                    sousLigne={vide ? undefined : `${nb(lavesOuInspectes)} / ${nb(k.parc)} ${lavage ? 'lavés' : 'inspectés'}`}
                    progression={vide ? undefined : chiffreCouverture}
                />
                <TuileKpi
                    tailleChiffre={26}
                    chiffre={k.enDefaut}
                    libelle="en défaut"
                    sousLigne={vide || k.inspectes === 0 ? undefined : `${pct(k.enDefaut / k.inspectes)} des inspectés`}
                    onClick={vide ? undefined : () => onKpi('pb')}
                />
                <TuileKpi
                    tailleChiffre={26}
                    chiffre={k.impossibles}
                    libelle="impossibles"
                    sousLigne="par motif : —"
                    onClick={vide ? undefined : () => onKpi('imp')}
                />
                <TuileKpi tailleChiffre={26} chiffre={k.photos} libelle="photos" sousLigne={ACTE_LIBELLES[acte]}/>
                </>
                )}
            </Box>

            {vide ? (
                <Box sx={{p: '16px', fontSize: 13, color: '#6B7480'}}>
                    Aucun PAV ne correspond à la combinaison de filtres. Les classements par point de contrôle et par commune s'affichent dès qu'il y a des résultats.
                </Box>
            ) : (
                <>
                    <Box sx={{p: '12px 16px', borderBottom: '1px solid #EEF1F4', display: 'flex', flexDirection: 'column', gap: '6px'}}>
                        <Box
                            role="img"
                            aria-label={`Répartition : ${VERDICTS.map((v) => `${parVerdict[v]} ${VERDICT_LIBELLES[v].toLowerCase()}`).join(', ')}`}
                            sx={{display: 'flex', height: 10, borderRadius: '6px', overflow: 'hidden', border: '1px solid #E3E7EC'}}
                        >
                            {VERDICTS.map((v) => parVerdict[v] > 0 && (
                                <Box
                                    key={v}
                                    component="span"
                                    sx={{
                                        flex: parVerdict[v],
                                        background: v === 'ni' ? 'repeating-linear-gradient(135deg, #FFFFFF 0 3px, #BDBDBD 3px 6px)' : VERDICT_COULEURS[v].pastille,
                                    }}
                                />
                            ))}
                        </Box>
                        <Box component="span" sx={{fontSize: 11, color: '#6B7480'}}>
                            {VERDICTS.map((v) => `${nb(parVerdict[v])} ${VERDICT_LIBELLES[v].toLowerCase()}`).join(' · ')}
                        </Box>
                    </Box>
                    <Classement titre="Défauts par point de contrôle" lignes={defauts} onClic={(l) => onElement(l.libelle)}/>
                    <Classement titre="Communes les plus touchées" lignes={communes} onClic={(l) => onCommune(l.cle)}/>
                </>
            )}
            <PavSelectionne selection={selection} acte={acte} onOuvrirFiche={onOuvrirFiche}/>
        </Box>
    );
}
